import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./BetaLayout.css";
import "./BetaPortalUI.css";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";
import { buildPortalHash, getPortalRoutePage } from "./betaRouting";
import {
    PortalExperienceContext,
    PortalPageActionsContext,
} from "./PortalExperience";
import {
    BETA_THEME_CHANGE_EVENT,
    clearBetaThemeBootstrap,
    getBetaEffectiveTheme,
    getBetaThemePreference,
} from "./betaTheme";
import PasskeyPrompt from "./PasskeyPrompt";
import GlobalSearch from "./GlobalSearch";
import NotificationCentre from "./NotificationCentre";

const BETA_SIDEBAR_COLLAPSED_STORAGE_KEY = "urban-portal-beta-sidebar-collapsed";
const FRONTEND_UPDATE_CHECK_INTERVAL_MS = 60_000;
const FRONTEND_UPDATE_SUPPRESS_MS = 60 * 60 * 1000;
const FRONTEND_UPDATE_SUPPRESS_KEY = "frontend_update_toast_suppressed_until";

function extractBuildIdFromPath(pathValue) {
    const value = String(pathValue || "");
    if (!value) return "";

    const mainMatch = value.match(/main\.([a-z0-9]+)\.(?:js|css)/i);
    if (mainMatch?.[1]) return mainMatch[1];

    const chunkMatch = value.match(/(?:^|\/)(?:static\/)?(?:js|css)\/[^/]*\.([a-z0-9]{6,})\.(?:js|css)/i);
    if (chunkMatch?.[1]) return chunkMatch[1];

    return value;
}

function getCurrentBuildIdFromDom() {
    if (typeof document === "undefined") return "";
    const scripts = Array.from(document.querySelectorAll("script[src]"));
    for (const script of scripts) {
        const src = String(script.getAttribute("src") || "");
        if (!src) continue;
        const buildId = extractBuildIdFromPath(src);
        if (buildId) return buildId;
    }
    return "";
}

function extractBuildIdFromManifest(manifest) {
    if (!manifest || typeof manifest !== "object") return "";
    const files = manifest.files && typeof manifest.files === "object"
        ? manifest.files
        : null;
    const mainJs = files?.["main.js"] || files?.["main.js.map"] || "";
    const viaMain = extractBuildIdFromPath(mainJs);
    if (viaMain) return viaMain;

    const fileValues = files ? Object.values(files) : [];
    for (const value of fileValues) {
        const buildId = extractBuildIdFromPath(value);
        if (buildId) return buildId;
    }
    return "";
}

async function fetchLatestBuildId() {
    const manifestUrl = new URL(
        `${process.env.PUBLIC_URL || ""}/asset-manifest.json`,
        window.location.origin
    );
    manifestUrl.searchParams.set("t", String(Date.now()));
    const response = await fetch(manifestUrl.toString(), { cache: "no-store" });
    if (!response.ok) return "";
    const manifest = await response.json().catch(() => null);
    return extractBuildIdFromManifest(manifest);
}

function BetaFrontendUpdateNotifier({ enabled }) {
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const [latestBuildId, setLatestBuildId] = useState("");
    const currentBuildIdRef = useRef("");

    useEffect(() => {
        currentBuildIdRef.current = getCurrentBuildIdFromDom();
    }, []);

    useEffect(() => {
        if (!enabled) {
            setIsUpdateAvailable(false);
            return undefined;
        }

        let cancelled = false;
        let inFlight = false;

        const isSuppressed = () => {
            try {
                const suppressedUntil = Number(
                    window.localStorage.getItem(FRONTEND_UPDATE_SUPPRESS_KEY) || 0
                );
                return Date.now() < suppressedUntil;
            } catch {
                return false;
            }
        };

        const checkForFrontendUpdate = async () => {
            if (cancelled || inFlight) return;
            inFlight = true;
            try {
                const nextBuildId = await fetchLatestBuildId();
                if (cancelled || !nextBuildId) return;

                setLatestBuildId(nextBuildId);
                if (!currentBuildIdRef.current) {
                    currentBuildIdRef.current = nextBuildId;
                    return;
                }

                if (
                    nextBuildId !== currentBuildIdRef.current &&
                    !isSuppressed()
                ) {
                    setIsUpdateAvailable(true);
                }
            } catch {
                // This is a best-effort check; transient failures are ignored.
            } finally {
                inFlight = false;
            }
        };

        checkForFrontendUpdate();
        const intervalId = window.setInterval(
            checkForFrontendUpdate,
            FRONTEND_UPDATE_CHECK_INTERVAL_MS
        );
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkForFrontendUpdate();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [enabled]);

    if (!enabled || !isUpdateAvailable) return null;

    const dismiss = () => {
        try {
            window.localStorage.setItem(
                FRONTEND_UPDATE_SUPPRESS_KEY,
                String(Date.now() + FRONTEND_UPDATE_SUPPRESS_MS)
            );
        } catch {
            // Dismissing still works for this page load if storage is unavailable.
        }
        setIsUpdateAvailable(false);
    };

    return (
        <div
            className="beta-update-toast-container"
            role="status"
            aria-live="polite"
        >
            <div className="beta-update-toast">
                <div className="beta-update-toast__copy">
                    <strong>New update available</strong>
                    <span>Refresh to use the latest version of the portal.</span>
                </div>
                <div className="beta-update-toast__actions">
                    <button
                        type="button"
                        className="beta-update-toast__refresh"
                        onClick={() => window.location.reload()}
                        title={latestBuildId ? `Latest build: ${latestBuildId}` : "Refresh now"}
                    >
                        Refresh now
                    </button>
                    <button
                        type="button"
                        className="beta-update-toast__dismiss"
                        onClick={dismiss}
                        aria-label="Dismiss update notification"
                        title="Dismiss"
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function getInitialSidebarCollapsed() {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(BETA_SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
    } catch {
        return false;
    }
}

const Icon = ({ children }) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        {children}
    </svg>
);

const icons = {
    dashboard: <Icon><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></Icon>,
   episodes: <Icon><path d="M4 5h16M4 12h16M4 19h10" /><circle cx="18" cy="19" r="2" /></Icon>,
    billing: <Icon><path d="M6 3h12v18l-6-3-6 3z" /><path d="M9 8h6M9 12h6" /></Icon>,
    podcasts: <Icon><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2" /><path d="M12 4v2M12 18v2" /></Icon>,
    analytics: <Icon><path d="M4 19V5M4 19h16" /><path d="m7 15 3-4 3 2 5-7" /></Icon>,
    clients: <Icon><circle cx="12" cy="8" r="3" /><path d="M5 20c.8-3.2 3.1-5 7-5s6.2 1.8 7 5" /></Icon>,
    files: <Icon><path d="M4 5h6l2 2h8v12H4z" /></Icon>,
    leads: <Icon><path d="M4 7h16M4 12h10M4 17h7" /><circle cx="18" cy="17" r="2" /></Icon>,
    meetings: <Icon><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></Icon>,
    recording: <Icon><rect x="3" y="5" width="14" height="14" rx="2" /><path d="m17 10 4-2v8l-4-2" /></Icon>,
    users: <Icon><circle cx="9" cy="8" r="3" /><path d="M3 20c.5-3 2.5-5 6-5s5.5 2 6 5M16 5a3 3 0 0 1 0 6M17 15c2.2.5 3.5 2.1 4 5" /></Icon>,
    settings: <Icon><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.04.04a2 2 0 1 1-2.83 2.83l-.04-.04A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 1.55V21a2 2 0 1 1-4 0v-.05a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1.9.31l-.04.04a2 2 0 1 1-2.83-2.83l.04-.04A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.05A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.31-1.9l-.04-.04a2 2 0 1 1 2.83-2.83l.04.04A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.55V3a2 2 0 1 1 4 0v.05a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.9-.31l.04-.04a2 2 0 1 1 2.83 2.83l-.04.04A1.7 1.7 0 0 0 19.4 9a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.05a1.7 1.7 0 0 0-1.55 1z" /></Icon>,
    menu: <Icon><path d="M4 7h16M4 12h16M4 17h16" /></Icon>,
    collapse: <Icon><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16M13 9h4M13 12h4M13 15h4" /></Icon>,
    arrow: <Icon><path d="M5 12h14M13 6l6 6-6 6" /></Icon>,
};

function getNavItems(user) {
    const isClient = user?.role === "client";
    const isAdmin = user?.role === "admin";
    const canFiles = isAdmin || Boolean(user?.has_file_browser_access);
   const canRecording = isAdmin || Boolean(user?.has_recording_sessions_access);
    const canBilling = isAdmin || Boolean(user?.has_billing_access);
    const canAnalytics = isAdmin || Boolean(user?.has_analytics_access);
    const items = [
        ["dashboard", "Dashboard", "dashboard"],
        ["episodes", "Episodes", "episodes"],
    ];
    if (!isClient) items.push(["podcasts", "Podcasts", "podcasts"]);
    if (canAnalytics) items.push(["analytics", "Analytics", "analytics"]);
   if (!isClient) items.push(["clients", "Clients", "clients"], ["leads", "Leads", "leads"], ["meetings", "Meetings", "meetings"]);
    if (isAdmin) items.push(['documents', 'Documents', 'files']);
    if (canRecording) items.push(["recording-sessions", "Recording Sessions", "recording"]);
    if (canFiles) items.push(["files", "File Explorer", "files"]);
    if (isAdmin) items.push(["user-management", "User Management", "users"]);
    if (canBilling) items.push(["billing", "Billing", "billing"]);
    items.push(["settings", "Settings", "settings"]);
    return items;
}

function getInitials(user) {
    const value = String(user?.first_name || user?.name || user?.email || "UP").trim();
    return value.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase().slice(0, 2) || "UP";
}

export default function BetaLayout({ children, onLogout, currentUser }) {
    const [theme, setTheme] = useState(() => getBetaEffectiveTheme(getBetaThemePreference()));
    const [pageActions, setPageActions] = useState(null);
    const [toolbarMounted, setToolbarMounted] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(getInitialSidebarCollapsed);
    // Page actions update after feature pages render. Keep that changing value
    // out of this context so pages that only read the portal variant stay idle.
    const portalExperience = useMemo(
        () => ({ setPageActions, setToolbarMounted, variant: "modern" }),
        [setPageActions, setToolbarMounted]
    );
    const navItems = getNavItems(currentUser);
    const currentPage = getPortalRoutePage(window.location.hash);
    const activeNavItem = navItems.find(([route]) => currentPage.startsWith(route)) || navItems[0];

    useLayoutEffect(() => {
        const themeClass = `beta-portal-theme-${theme}`;
        const pageRoots = [document.documentElement, document.body];
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        pageRoots.forEach((element) => {
            element.classList.add("beta-portal-active");
            element.classList.remove("beta-portal-theme-light", "beta-portal-theme-dark");
            element.classList.add(themeClass);
        });
        themeColorMeta?.setAttribute("content", theme === "dark" ? "#101010" : "#f7f7f7");
        clearBetaThemeBootstrap();
        return () => {
            pageRoots.forEach((element) => {
                element.classList.remove(
                    "beta-portal-active",
                    "beta-portal-theme-light",
                    "beta-portal-theme-dark"
                );
            });
            if (themeColorMeta) {
                themeColorMeta.setAttribute("content", "#020617");
            }
        };
    }, [theme]);

    useEffect(() => {
        try {
            window.localStorage.setItem(BETA_SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
        } catch {
            // The sidebar still works when browser storage is unavailable.
        }
    }, [collapsed]);

    useEffect(() => {
        const updateTheme = (preference = getBetaThemePreference()) => {
            setTheme(getBetaEffectiveTheme(preference));
        };
        const handleThemeChange = (event) => updateTheme(event.detail?.preference);
        const mediaQuery = typeof window.matchMedia === "function"
            ? window.matchMedia("(prefers-color-scheme: dark)")
            : null;
        const handleSystemThemeChange = () => {
            if (getBetaThemePreference() === "system") updateTheme("system");
        };

        window.addEventListener(BETA_THEME_CHANGE_EVENT, handleThemeChange);
        mediaQuery?.addEventListener?.("change", handleSystemThemeChange);
        return () => {
            window.removeEventListener(BETA_THEME_CHANGE_EVENT, handleThemeChange);
            mediaQuery?.removeEventListener?.("change", handleSystemThemeChange);
        };
    }, []);

    return (
        <PortalExperienceContext.Provider value={portalExperience}>
            <PortalPageActionsContext.Provider value={pageActions}>
            <div className={`beta-shell beta-shell--${theme}`}>
            <aside className={`beta-sidebar${mobileOpen ? " beta-sidebar--open" : ""}${collapsed ? " beta-sidebar--collapsed" : ""}`}>
                <div className="beta-brand">
                    <img
                        src={theme === "dark" ? logoLight : logoDark}
                        alt="Urban Podcasts"
                        className="beta-brand__logo beta-sidebar__fade"
                    />
                </div>
                <nav className="beta-nav" aria-label="Portal navigation">
                    {navItems.map(([route, label, icon]) => (
                        <a key={route} href={buildPortalHash(route)} className={`beta-nav__item${currentPage.startsWith(route) ? " is-active" : ""}`} title={collapsed ? label : undefined} onClick={() => setMobileOpen(false)}>
                            <span className="beta-nav__icon" aria-hidden="true">{icons[icon]}</span>
                            <span className="beta-nav__text beta-sidebar__fade">{label}</span>
                        </a>
                    ))}
                </nav>
            </aside>
            {mobileOpen && <button type="button" className="beta-overlay" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
            <main className="beta-main">
                <header className="beta-topbar">
                    <button type="button" className="beta-icon-button beta-menu-button" aria-label="Open navigation" title="Open navigation" onClick={() => setMobileOpen(true)}>{icons.menu}</button>
                    <div className="beta-topbar__context">
                        <button type="button" className={`beta-collapse-control${collapsed ? " is-collapsed" : ""}`} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} title={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-expanded={!collapsed} onClick={() => setCollapsed((current) => !current)}>{icons.collapse}</button>
                        <span className="beta-topbar__page-title">{activeNavItem?.[1] || "Dashboard"}</span>
                    </div>
                    <div className="beta-topbar__actions">
                        {!toolbarMounted && pageActions ? (
                            <div className="beta-topbar__page-actions">{pageActions}</div>
                        ) : null}
                        <GlobalSearch currentUser={currentUser} variant="modern" />
                        <NotificationCentre currentUser={currentUser} variant="modern" />
                        <button type="button" className="beta-user-button" aria-label="User account" title="User account"><span className="beta-avatar">{getInitials(currentUser)}</span><span className="beta-user-button__text">{currentUser?.first_name || currentUser?.name || currentUser?.email || "Team"}</span></button>
                        <button type="button" className="beta-logout-button" onClick={onLogout}>
                            {icons.arrow}
                            <span>Log out</span>
                        </button>
                    </div>
                </header>
                <div className="beta-content">{children}</div>
            </main>
            {currentUser?.id && <PasskeyPrompt user={currentUser} />}
            <BetaFrontendUpdateNotifier enabled={currentUser?.role === "admin"} />
            </div>
            </PortalPageActionsContext.Provider>
        </PortalExperienceContext.Provider>
    );
}
